/**
 * Index Route
 *
 * Root "/" route with conditional rendering:
 * - Authenticated users → redirect to /dashboard
 * - Unauthenticated users → marketing landing page
 */

import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { IconContext } from "@phosphor-icons/react";
import { useAuth } from "../stores/authStore";
import { LandingPage } from "../components/landing/LandingPage";
import { useSEO } from "../hooks/useSEO";
import { SEO_CONFIG } from "../seo/seoConfig";

export const Route = createFileRoute("/")({
  beforeLoad: ({ context }) => {
    const { auth } = context;

    // Authenticated users always go to clients
    if (auth.isAuthenticated) {
      throw redirect({ to: "/clients" });
    }
    // Loading or unauthenticated: let component handle it
  },
  component: IndexPage,
});

function IndexPage() {
  // Use React context directly for reactive auth state updates.
  // Route.useRouteContext() reads from the router context which may not
  // trigger component re-renders when auth state changes.
  const auth = useAuth();
  const navigate = useNavigate();

  // Page title, description, canonical, OG/Twitter tags — with cleanup on
  // unmount (Story 30.2 useSEO; replaces the hand-rolled title/description).
  useSEO(SEO_CONFIG["/"]!);

  // Component-level guard for race condition during auth loading
  useEffect(() => {
    if (auth.isAuthenticated) {
      navigate({ to: "/clients" });
    }
  }, [auth.isAuthenticated, navigate]);

  // Show minimal loading state while auth resolves
  if (auth.isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand border-t-transparent" />
      </div>
    );
  }

  return (
    <IconContext.Provider value={{ weight: "duotone" }}>
      <div className="duotone-icons contents">
        <LandingPage />
      </div>
    </IconContext.Provider>
  );
}
